import { concatBytes, copyBytes, randomBytes } from "./bytes.js";
import { dialOnce } from "./client.js";
import type { BlindConnection } from "./connection.js";
import { BlindBindError } from "./errors.js";
import type { Route } from "./http.js";
import { ByteStream } from "./stream.js";
import type { WebSocketFactory, WebSocketLike } from "./websocket.js";

const headerTerminator = new TextEncoder().encode("\r\n\r\n");
const webSocketGUID = "258EAFA5-E914-47DA-95CA-C5AB0DC85B11";
const maximumHeaderBytes = 64 << 10;
const defaultMaximumMessageBytes = 4 << 20;

export interface MessageSocketCloseInfo {
  code: number;
  reason: string;
  clean: boolean;
}

export interface MessageSocket {
  readonly readyState: 0 | 1 | 2 | 3;
  readonly protocol: string;
  readonly closed: Promise<MessageSocketCloseInfo>;

  send(message: string): void;
  close(code?: number, reason?: string): void;
  onMessage(listener: (message: string) => void): () => void;
  onClose(listener: (info: MessageSocketCloseInfo) => void): () => void;
  onError(listener: (error: Error) => void): () => void;
}

export interface OpenSocketOptions {
  protocols?: readonly string[];
  signal?: AbortSignal;
  handshakeTimeoutMs?: number;
  maximumMessageBytes?: number;
  webSocketFactory?: WebSocketFactory;
}

export async function openSocket(route: Route, path: string, options: OpenSocketOptions = {}): Promise<MessageSocket> {
  const normalizedPath = normalizeSocketPath(path);
  if (route.type === "direct") return openDirectSocket(route.origin, normalizedPath, options);
  const maximumMessageBytes = options.maximumMessageBytes ?? defaultMaximumMessageBytes;
  if (!Number.isSafeInteger(maximumMessageBytes) || maximumMessageBytes < 1) {
    throw new Error("Maximum WebSocket message size must be a positive integer");
  }
  const connection = await dialOnce({
    endpoint: route.endpoint,
    ...(options.signal ? { signal: options.signal } : {}),
    ...(options.handshakeTimeoutMs !== undefined ? { handshakeTimeoutMs: options.handshakeTimeoutMs } : {}),
    ...(options.webSocketFactory ? { webSocketFactory: options.webSocketFactory } : {}),
  });
  try {
    const opening = RelayMessageSocket.open(connection, normalizedPath, options.protocols ?? [], maximumMessageBytes);
    const socket = await guardOpening(opening, connection, options.signal, options.handshakeTimeoutMs ?? 10_000);
    bindAbort(socket, options.signal);
    return socket;
  } catch (error) {
    connection.close();
    throw error;
  }
}

async function openDirectSocket(origin: string, path: string, options: OpenSocketOptions): Promise<MessageSocket> {
  const url = new URL(origin);
  if (url.protocol !== "http:" && url.protocol !== "https:") throw new Error("Direct route must use HTTP or HTTPS");
  if (url.username || url.password || url.search || url.hash || (url.pathname !== "" && url.pathname !== "/")) {
    throw new Error("Direct route must be an origin without credentials, query, fragment, or path");
  }
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = new URL(path, "http://blind.invalid").pathname;
  url.search = new URL(path, "http://blind.invalid").search;
  const socket =
    options.webSocketFactory?.connect(url.toString(), options.protocols) ??
    defaultSocket(url.toString(), options.protocols);
  socket.binaryType = "arraybuffer";
  await waitForOpen(socket, options.signal, options.handshakeTimeoutMs ?? 10_000);
  const opened = new DirectMessageSocket(socket);
  bindAbort(opened, options.signal);
  return opened;
}

class DirectMessageSocket implements MessageSocket {
  readonly #socket: WebSocketLike;
  readonly #messages = new Set<(message: string) => void>();
  readonly #closes = new Set<(info: MessageSocketCloseInfo) => void>();
  readonly #errors = new Set<(error: Error) => void>();
  readonly closed: Promise<MessageSocketCloseInfo>;
  #resolveClosed: (info: MessageSocketCloseInfo) => void = () => undefined;
  #closeInfo: MessageSocketCloseInfo | null = null;

  constructor(socket: WebSocketLike) {
    this.#socket = socket;
    this.closed = new Promise((resolve) => {
      this.#resolveClosed = resolve;
    });
    socket.addEventListener("message", this.#handleMessage);
    socket.addEventListener("close", this.#handleClose);
    socket.addEventListener("error", this.#handleError);
  }

  get readyState(): 0 | 1 | 2 | 3 {
    return socketReadyState(this.#socket.readyState);
  }

  get protocol(): string {
    return this.#socket.protocol ?? "";
  }

  send(message: string): void {
    this.#socket.send(message);
  }

  close(code?: number, reason?: string): void {
    this.#socket.close(code, reason);
  }

  onMessage(listener: (message: string) => void): () => void {
    this.#messages.add(listener);
    return () => this.#messages.delete(listener);
  }

  onClose(listener: (info: MessageSocketCloseInfo) => void): () => void {
    this.#closes.add(listener);
    if (this.#closeInfo) queueMicrotask(() => listener(this.#closeInfo ?? { code: 1006, reason: "", clean: false }));
    return () => this.#closes.delete(listener);
  }

  onError(listener: (error: Error) => void): () => void {
    this.#errors.add(listener);
    return () => this.#errors.delete(listener);
  }

  readonly #handleMessage: EventListener = (event) => {
    if (!(event instanceof MessageEvent)) return;
    if (typeof event.data !== "string") {
      this.#emitError(new BlindBindError("protocol_error", "Nest WebSocket returned a binary message"));
      this.close(1003, "text messages required");
      return;
    }
    for (const listener of this.#messages) listener(event.data);
  };

  readonly #handleClose: EventListener = (event) => {
    const close = event instanceof CloseEvent ? event : null;
    const info = { code: close?.code ?? 1006, reason: close?.reason ?? "", clean: close?.wasClean ?? false };
    this.#closeInfo = info;
    this.#resolveClosed(info);
    for (const listener of this.#closes) listener(info);
    this.#detach();
  };

  readonly #handleError: EventListener = () => {
    this.#emitError(new BlindBindError("connection_failed", "WebSocket connection failed"));
  };

  #emitError(error: Error): void {
    for (const listener of this.#errors) listener(error);
  }

  #detach(): void {
    this.#socket.removeEventListener("message", this.#handleMessage);
    this.#socket.removeEventListener("close", this.#handleClose);
    this.#socket.removeEventListener("error", this.#handleError);
  }
}

class RelayMessageSocket implements MessageSocket {
  readonly #connection: BlindConnection;
  readonly #reader: ByteStream;
  readonly #writer: WritableStreamDefaultWriter<Uint8Array>;
  readonly #maximumMessageBytes: number;
  readonly #messages = new Set<(message: string) => void>();
  readonly #closes = new Set<(info: MessageSocketCloseInfo) => void>();
  readonly #errors = new Set<(error: Error) => void>();
  readonly closed: Promise<MessageSocketCloseInfo>;
  readonly protocol: string;
  #resolveClosed: (info: MessageSocketCloseInfo) => void = () => undefined;
  #readyState: 0 | 1 | 2 | 3 = 1;
  #sendTail: Promise<void> = Promise.resolve();
  #closeInfo: MessageSocketCloseInfo | null = null;
  #closeTimer: ReturnType<typeof setTimeout> | null = null;

  private constructor(
    connection: BlindConnection,
    reader: ByteStream,
    writer: WritableStreamDefaultWriter<Uint8Array>,
    protocol: string,
    maximumMessageBytes: number,
  ) {
    this.#connection = connection;
    this.#reader = reader;
    this.#writer = writer;
    this.protocol = protocol;
    this.#maximumMessageBytes = maximumMessageBytes;
    this.closed = new Promise((resolve) => {
      this.#resolveClosed = resolve;
    });
    void this.#readLoop();
  }

  static async open(
    connection: BlindConnection,
    path: string,
    protocols: readonly string[],
    maximumMessageBytes: number,
  ): Promise<RelayMessageSocket> {
    validateProtocols(protocols);
    const reader = new ByteStream(connection.readable);
    const writer = connection.writable.getWriter();
    const key = encodeBase64(randomBytes(16));
    const headers = [
      `GET ${path} HTTP/1.1`,
      "Host: blind.invalid",
      "Upgrade: websocket",
      "Connection: Upgrade",
      `Sec-WebSocket-Key: ${key}`,
      "Sec-WebSocket-Version: 13",
    ];
    if (protocols.length > 0) headers.push(`Sec-WebSocket-Protocol: ${protocols.join(", ")}`);
    await writer.write(new TextEncoder().encode(`${headers.join("\r\n")}\r\n\r\n`));
    const response = parseUpgradeResponse(await reader.readUntil(headerTerminator, maximumHeaderBytes));
    if (response.status !== 101)
      throw new BlindBindError("connection_failed", `WebSocket upgrade failed (${response.status})`);
    if (
      !headerContainsToken(response.headers, "Upgrade", "websocket") ||
      !headerContainsToken(response.headers, "Connection", "upgrade")
    ) {
      throw new BlindBindError("protocol_error", "Peer returned an invalid WebSocket upgrade response");
    }
    const expectedAccept = encodeBase64(
      new Uint8Array(await crypto.subtle.digest("SHA-1", new TextEncoder().encode(key + webSocketGUID))),
    );
    if (response.headers.get("Sec-WebSocket-Accept") !== expectedAccept) {
      throw new BlindBindError("protocol_error", "Peer WebSocket identity check failed");
    }
    const protocol = response.headers.get("Sec-WebSocket-Protocol") ?? "";
    if (protocol && !protocols.includes(protocol)) {
      throw new BlindBindError("protocol_error", "Peer selected an unrequested WebSocket protocol");
    }
    return new RelayMessageSocket(connection, reader, writer, protocol, maximumMessageBytes);
  }

  get readyState(): 0 | 1 | 2 | 3 {
    return this.#readyState;
  }

  send(message: string): void {
    if (this.#readyState !== 1) throw new BlindBindError("closed", "WebSocket is not open");
    const payload = new TextEncoder().encode(message);
    if (payload.byteLength > this.#maximumMessageBytes)
      throw new Error("WebSocket message exceeds the configured maximum");
    this.#queueFrame(0x1, payload);
  }

  close(code = 1000, reason = ""): void {
    if (this.#readyState >= 2) return;
    validateClose(code, reason);
    this.#readyState = 2;
    this.#queueFrame(0x8, closePayload(code, reason));
    this.#closeTimer = setTimeout(() => this.#finish({ code: 1006, reason: "close timeout", clean: false }), 1_000);
  }

  onMessage(listener: (message: string) => void): () => void {
    this.#messages.add(listener);
    return () => this.#messages.delete(listener);
  }

  onClose(listener: (info: MessageSocketCloseInfo) => void): () => void {
    this.#closes.add(listener);
    if (this.#closeInfo) queueMicrotask(() => listener(this.#closeInfo ?? { code: 1006, reason: "", clean: false }));
    return () => this.#closes.delete(listener);
  }

  onError(listener: (error: Error) => void): () => void {
    this.#errors.add(listener);
    return () => this.#errors.delete(listener);
  }

  async #readLoop(): Promise<void> {
    let fragmented: Uint8Array[] | null = null;
    let fragmentedBytes = 0;
    try {
      while (this.#readyState !== 3) {
        const frame = await readFrame(this.#reader, this.#maximumMessageBytes);
        if (frame.opcode === 0x8) {
          const info = parseClosePayload(frame.payload);
          if (this.#readyState === 1) {
            this.#readyState = 2;
            await this.#writeFrame(0x8, frame.payload);
          }
          this.#finish({ ...info, clean: true });
          return;
        }
        if (frame.opcode === 0x9) {
          await this.#writeFrame(0xa, frame.payload);
          continue;
        }
        if (frame.opcode === 0xa) continue;
        if (frame.opcode === 0x2)
          throw new BlindBindError("protocol_error", "Nest WebSocket returned a binary message");
        if (frame.opcode === 0x1) {
          if (fragmented) throw new BlindBindError("protocol_error", "Peer interleaved fragmented WebSocket messages");
          if (frame.final) {
            this.#emitMessage(decodeMessage(frame.payload));
          } else {
            fragmented = [frame.payload];
            fragmentedBytes = frame.payload.byteLength;
          }
          continue;
        }
        if (frame.opcode !== 0x0 || !fragmented)
          throw new BlindBindError("protocol_error", "Peer sent an invalid WebSocket continuation");
        fragmentedBytes += frame.payload.byteLength;
        if (fragmentedBytes > this.#maximumMessageBytes)
          throw new BlindBindError("protocol_error", "WebSocket message is too large");
        fragmented.push(frame.payload);
        if (frame.final) {
          this.#emitMessage(decodeMessage(concatMany(fragmented, fragmentedBytes)));
          fragmented = null;
          fragmentedBytes = 0;
        }
      }
    } catch (error) {
      if (this.#readyState === 3) return;
      const normalized = error instanceof Error ? error : new Error(String(error));
      this.#emitError(normalized);
      this.#finish({ code: 1006, reason: normalized.message, clean: false });
    }
  }

  #queueFrame(opcode: number, payload: Uint8Array): void {
    const operation = this.#sendTail.then(() => this.#writeFrame(opcode, payload));
    this.#sendTail = operation.catch((error: unknown) => {
      const normalized = error instanceof Error ? error : new Error(String(error));
      this.#emitError(normalized);
      this.#finish({ code: 1006, reason: normalized.message, clean: false });
    });
  }

  #writeFrame(opcode: number, payload: Uint8Array): Promise<void> {
    return this.#writer.write(encodeClientFrame(opcode, payload));
  }

  #emitMessage(message: string): void {
    for (const listener of this.#messages) listener(message);
  }

  #emitError(error: Error): void {
    for (const listener of this.#errors) listener(error);
  }

  #finish(info: MessageSocketCloseInfo): void {
    if (this.#readyState === 3) return;
    this.#readyState = 3;
    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    this.#closeInfo = info;
    this.#connection.close();
    this.#resolveClosed(info);
    for (const listener of this.#closes) listener(info);
  }
}

interface WebSocketFrame {
  final: boolean;
  opcode: number;
  payload: Uint8Array;
}

async function readFrame(reader: ByteStream, maximumMessageBytes: number): Promise<WebSocketFrame> {
  const header = await reader.readExactly(2);
  const first = header[0] ?? 0;
  const second = header[1] ?? 0;
  if ((first & 0x70) !== 0) throw new BlindBindError("protocol_error", "Peer used unsupported WebSocket extensions");
  if ((second & 0x80) !== 0) throw new BlindBindError("protocol_error", "Server WebSocket frames must not be masked");
  const opcode = first & 0x0f;
  const final = (first & 0x80) !== 0;
  let length = second & 0x7f;
  if (length === 126) {
    length = new DataView((await reader.readExactly(2)).buffer).getUint16(0, false);
  } else if (length === 127) {
    const extended = new DataView((await reader.readExactly(8)).buffer).getBigUint64(0, false);
    if (extended > BigInt(Number.MAX_SAFE_INTEGER))
      throw new BlindBindError("protocol_error", "WebSocket frame is too large");
    length = Number(extended);
  }
  const control = opcode >= 0x8;
  if ((control && (!final || length > 125)) || length > maximumMessageBytes) {
    throw new BlindBindError("protocol_error", "Peer sent an invalid or oversized WebSocket frame");
  }
  return { final, opcode, payload: await reader.readExactly(length) };
}

function encodeClientFrame(opcode: number, payload: Uint8Array): Uint8Array {
  const lengthBytes = payload.byteLength < 126 ? 0 : payload.byteLength <= 0xffff ? 2 : 8;
  const header = new Uint8Array(2 + lengthBytes + 4);
  header[0] = 0x80 | opcode;
  header[1] = 0x80 | (lengthBytes === 0 ? payload.byteLength : lengthBytes === 2 ? 126 : 127);
  if (lengthBytes === 2) new DataView(header.buffer).setUint16(2, payload.byteLength, false);
  if (lengthBytes === 8) new DataView(header.buffer).setBigUint64(2, BigInt(payload.byteLength), false);
  const maskOffset = 2 + lengthBytes;
  const mask = randomBytes(4);
  header.set(mask, maskOffset);
  const masked = copyBytes(payload);
  for (let index = 0; index < masked.byteLength; index += 1) {
    masked[index] = (masked[index] ?? 0) ^ (mask[index % 4] ?? 0);
  }
  return concatBytes(header, masked);
}

function parseUpgradeResponse(value: Uint8Array): { status: number; headers: Headers } {
  const text = new TextDecoder().decode(value.subarray(0, value.byteLength - headerTerminator.byteLength));
  const lines = text.split("\r\n");
  const statusMatch = /^HTTP\/1\.[01] ([0-9]{3})(?: .*)?$/.exec(lines.shift() ?? "");
  if (!statusMatch) throw new BlindBindError("protocol_error", "Peer returned an invalid WebSocket status line");
  const headers = new Headers();
  for (const line of lines) {
    const separator = line.indexOf(":");
    if (separator < 1) throw new BlindBindError("protocol_error", "Peer returned an invalid WebSocket header");
    headers.append(line.slice(0, separator).trim(), line.slice(separator + 1).trim());
  }
  return { status: Number(statusMatch[1]), headers };
}

function headerContainsToken(headers: Headers, name: string, expected: string): boolean {
  return (headers.get(name) ?? "").split(",").some((value) => value.trim().toLowerCase() === expected.toLowerCase());
}

function parseClosePayload(payload: Uint8Array): MessageSocketCloseInfo {
  if (payload.byteLength === 0) return { code: 1005, reason: "", clean: true };
  if (payload.byteLength === 1)
    throw new BlindBindError("protocol_error", "Peer sent an invalid WebSocket close frame");
  const code = new DataView(payload.buffer, payload.byteOffset, payload.byteLength).getUint16(0, false);
  return { code, reason: decodeMessage(payload.subarray(2)), clean: true };
}

function closePayload(code: number, reason: string): Uint8Array {
  const text = new TextEncoder().encode(reason);
  const payload = new Uint8Array(2 + text.byteLength);
  new DataView(payload.buffer).setUint16(0, code, false);
  payload.set(text, 2);
  return payload;
}

function validateClose(code: number, reason: string): void {
  if (
    !Number.isInteger(code) ||
    code < 1000 ||
    code >= 5000 ||
    code === 1004 ||
    code === 1005 ||
    code === 1006 ||
    code === 1015
  ) {
    throw new Error("Invalid WebSocket close code");
  }
  if (new TextEncoder().encode(reason).byteLength > 123) throw new Error("WebSocket close reason is too long");
}

function validateProtocols(protocols: readonly string[]): void {
  const seen = new Set<string>();
  for (const protocol of protocols) {
    if (!/^[!#$%&'*+\-.0-9A-Z^_`a-z|~]+$/.test(protocol) || seen.has(protocol)) {
      throw new Error("WebSocket protocols must contain unique HTTP tokens");
    }
    seen.add(protocol);
  }
}

function decodeMessage(payload: Uint8Array): string {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(payload);
  } catch (error) {
    throw new BlindBindError("protocol_error", "WebSocket text message is not valid UTF-8", { cause: error });
  }
}

function normalizeSocketPath(value: string): string {
  const url = new URL(value, "http://blind.invalid");
  if (url.origin !== "http://blind.invalid" || url.username || url.password || url.hash) {
    throw new Error("WebSocket path must be an absolute path without an origin, credentials, or fragment");
  }
  return `${url.pathname}${url.search}`;
}

function defaultSocket(url: string, protocols: readonly string[] | undefined): WebSocketLike {
  const Socket = globalThis.WebSocket;
  if (!Socket) throw new BlindBindError("connection_failed", "WebSocket is unavailable in this runtime");
  return new Socket(url, protocols ? [...protocols] : undefined);
}

async function waitForOpen(socket: WebSocketLike, signal: AbortSignal | undefined, timeoutMs: number): Promise<void> {
  if (signal?.aborted) {
    socket.close();
    throw new DOMException("Operation aborted", "AbortError");
  }
  if (socket.readyState === 1) return;
  await new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timeout);
      signal?.removeEventListener("abort", aborted);
      socket.removeEventListener("open", opened);
      socket.removeEventListener("close", closed);
      socket.removeEventListener("error", failed);
    };
    const opened: EventListener = () => {
      cleanup();
      resolve();
    };
    const closed: EventListener = () => {
      cleanup();
      reject(new BlindBindError("connection_failed", "WebSocket closed before opening"));
    };
    const failed: EventListener = () => {
      cleanup();
      reject(new BlindBindError("connection_failed", "WebSocket connection failed"));
    };
    const aborted = () => {
      cleanup();
      socket.close();
      reject(new DOMException("Operation aborted", "AbortError"));
    };
    const timeout = setTimeout(() => {
      cleanup();
      socket.close();
      reject(new BlindBindError("connection_failed", "WebSocket connection timed out"));
    }, timeoutMs);
    socket.addEventListener("open", opened);
    socket.addEventListener("close", closed);
    socket.addEventListener("error", failed);
    signal?.addEventListener("abort", aborted, { once: true });
  });
}

function socketReadyState(value: number): 0 | 1 | 2 | 3 {
  if (value === 0 || value === 1 || value === 2 || value === 3) return value;
  return 3;
}

async function guardOpening(
  opening: Promise<MessageSocket>,
  connection: BlindConnection,
  signal: AbortSignal | undefined,
  timeoutMs: number,
): Promise<MessageSocket> {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) throw new Error("WebSocket handshake timeout must be positive");
  if (signal?.aborted) {
    connection.close();
    throw new DOMException("Operation aborted", "AbortError");
  }
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let abort: (() => void) | undefined;
  const interrupted = new Promise<never>((_resolve, reject) => {
    abort = () => {
      connection.close({ reason: "operation aborted" });
      reject(new DOMException("Operation aborted", "AbortError"));
    };
    signal?.addEventListener("abort", abort, { once: true });
    timeout = setTimeout(() => {
      connection.close({ reason: "handshake timeout" });
      reject(new BlindBindError("connection_failed", "WebSocket upgrade timed out"));
    }, timeoutMs);
  });
  try {
    return await Promise.race([opening, interrupted]);
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
    if (abort) signal?.removeEventListener("abort", abort);
  }
}

function bindAbort(socket: MessageSocket, signal: AbortSignal | undefined): void {
  if (!signal) return;
  const abort = () => socket.close(1000, "operation aborted");
  if (signal.aborted) {
    abort();
    return;
  }
  signal.addEventListener("abort", abort, { once: true });
  void socket.closed.then(() => signal.removeEventListener("abort", abort));
}

function encodeBase64(value: Uint8Array): string {
  let binary = "";
  for (const byte of value) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function concatMany(chunks: Uint8Array[], total: number): Uint8Array {
  const result = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return result;
}
